<template>
  <v-app>
    <!-- Sticky Navbar -->
    <v-app-bar
      :elevation="scrolled ? 6 : 0"
      :color="scrolled ? 'surface' : 'transparent'"
      app
      style="transition: background-color 0.35s ease, box-shadow 0.35s ease"
    >
      <v-container class="d-flex align-center py-0" style="max-width: 1200px">
        <span class="nav-brand" @click="scrollTo('home')">
          HULL ZOMBIE BJJ
        </span>
        <v-spacer />
        <!-- Desktop links -->
        <div class="d-none d-md-flex align-center">
          <v-btn variant="text" class="nav-link" @click="scrollTo('beginners')">Beginners</v-btn>
          <v-btn variant="text" class="nav-link" @click="scrollTo('coach')">Coach</v-btn>
          <v-btn variant="text" class="nav-link" @click="scrollTo('gallery')">Gallery</v-btn>
          <v-btn variant="text" class="nav-link" @click="scrollTo('schedule')">Pricing</v-btn>
          <v-btn variant="text" class="nav-link" @click="scrollTo('faq')">FAQ</v-btn>
          <v-btn
            color="red-darken-2"
            rounded="pill"
            class="ml-3 font-weight-bold"
            @click="scrollTo('schedule')"
          >Book Your First Class</v-btn>
        </div>
        <!-- Mobile hamburger -->
        <v-app-bar-nav-icon class="d-md-none" @click="drawer = true" />
      </v-container>
    </v-app-bar>

    <!-- Mobile Drawer -->
    <v-navigation-drawer v-model="drawer" location="right" temporary width="260">
      <v-list nav class="py-8 px-4">
        <div class="nav-drawer-brand mb-8">HULL ZOMBIE BJJ</div>
        <v-list-item
          v-for="link in navLinks"
          :key="link.id"
          :title="link.label"
          class="mb-2 rounded"
          @click="scrollTo(link.id); drawer = false"
        />
        <v-btn
          color="red-darken-2"
          rounded="pill"
          block
          class="mt-6"
          @click="scrollTo('schedule'); drawer = false"
        >Book Your First Class</v-btn>
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <router-view />
    </v-main>

    <!-- Footer -->
    <v-footer style="background: #090e19; padding: 48px 0">
      <v-container>
        <v-row justify="center" align="center">
          <v-col cols="12" class="text-center mb-3">
            <div class="footer-brand">HULL ZOMBIE BJJ</div>
            <div class="footer-location mt-1">Hull, East Yorkshire</div>
          </v-col>
          <v-col cols="12" class="d-flex justify-center ga-1">
            <v-btn
              :icon="true"
              href="https://www.instagram.com/hullzombie.bjj/"
              target="_blank"
              variant="text"
              size="large"
            >
              <v-icon size="24">mdi-instagram</v-icon>
            </v-btn>
            <v-btn
              :icon="true"
              href="https://www.facebook.com/martyn.oliver.75"
              target="_blank"
              variant="text"
              size="large"
            >
              <v-icon size="24">mdi-facebook</v-icon>
            </v-btn>
            <v-btn
              :icon="true"
              href="https://www.tiktok.com/@hullzombie.bjj"
              target="_blank"
              variant="text"
              size="large"
            >
              <v-icon size="24">
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path
                    d="M12.74,2C12.74,2 12.75,6.79 17.03,7.14V9.33C15.44,9.22 14.26,8.54 13.48,7.41V14.9C13.48,18.11 11.33,20.17 8.5,20.17C5.67,20.17 3.52,18.1 3.52,14.9C3.52,12.02 5.42,9.95 8.03,9.65V12.05C6.98,12.32 6.27,13.18 6.27,14.25C6.27,15.5 7.18,16.41 8.48,16.41C9.78,16.41 10.67,15.53 10.67,14.2V2H12.74Z"
                  />
                </svg>
              </v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" class="text-center mt-4">
            <span style="font-size: 0.78rem; color: rgba(255,255,255,0.3)">
              &copy; 2025 Hull Zombie BJJ. All rights reserved.
            </span>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount } from "vue";
import { Lit } from "litlyx-js";

export default defineComponent({
  name: "App",
  setup() {
    const scrolled = ref(false);
    const drawer = ref(false);

    const navLinks = [
      { id: "beginners", label: "Beginners" },
      { id: "coach", label: "Meet the Coach" },
      { id: "gallery", label: "Gallery" },
      { id: "schedule", label: "Schedule & Pricing" },
      { id: "personal-training", label: "Private Coaching" },
      { id: "faq", label: "FAQ" },
    ];

    const handleScroll = () => {
      scrolled.value = window.scrollY > 60;
    };

    const scrollTo = (id) => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);
      Lit.init("683c8b537f61ebd3d8cbd538");
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    return { scrolled, drawer, navLinks, scrollTo };
  },
});
</script>

<style scoped>
.nav-brand {
  font-size: 1.1rem;
  font-weight: 900;
  letter-spacing: 3px;
  color: #ffffff;
  cursor: pointer;
  text-transform: uppercase;
  user-select: none;
}

.nav-link {
  letter-spacing: 1px;
  font-size: 0.82rem;
  text-transform: uppercase;
}

.nav-drawer-brand {
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 3px;
  color: #fff;
  text-transform: uppercase;
}

.footer-brand {
  font-size: 1.15rem;
  font-weight: 900;
  letter-spacing: 5px;
  color: #ffffff;
  text-transform: uppercase;
}

.footer-location {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.38);
  letter-spacing: 1px;
}
</style>
